import { useCallback } from "react";

import { shallow } from "zustand/shallow";

import useMeetingReportHighlights from "@app/analytics/MeetingReport/hooks/useMeetingReportHighlights";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import logger from "@app/utils/logger";

import { useMeetingReportContext } from "../providers/MeetingReportContext";

const videoIsFullScreen = () =>
  document?.fullscreenElement?.tagName === "VIDEO";

const useVideoControls = () => {
  const { highlights } = useMeetingReportHighlights();
  const { video } = useMeetingReportContext();
  const {
    muted,
    paused,
    setCurrentTimestamp,
    setMuted,
    setPaused,
    setPlaybackRate,
    setVideoReel,
    setVideoReelClipIndex,
    setVideoStartAt,
    videoReel,
    videoReelClipIndex,
  } = useVideoStore(
    (state) => ({
      muted: state.muted,
      paused: state.paused,
      setCurrentTimestamp: state.setCurrentTimestamp,
      setMuted: state.setMuted,
      setPaused: state.setPaused,
      setPlaybackRate: state.setPlaybackRate,
      setVideoReel: state.setVideoReel,
      setVideoReelClipIndex: state.setVideoReelClipIndex,
      setVideoStartAt: state.setVideoStartAt,
      videoReel: state.videoReel,
      videoReelClipIndex: state.videoReelClipIndex,
    }),
    shallow
  );

  const fullScreenEnabled =
    "fullscreenEnabled" in document && document.fullscreenEnabled;

  const pause = useCallback(() => {
    if (!video || video.paused || videoIsFullScreen()) {
      // pausing is handled natively by the video player in fullscreen, so
      // we only need to update the state value
      setPaused(true);
      return;
    }

    video.pause();
    setPaused(true);
  }, [video]);

  const play = useCallback(() => {
    if (!video || !video.paused || videoIsFullScreen()) {
      // playing is handled natively by the video player in fullscreen, so
      // we only need to update the state value
      setPaused(false);
      return;
    }

    try {
      video.play().then(() => setPaused(false));
    } catch (error) {
      logger.error("Error playing video");
      logger.error(error);
    }
  }, [video]);

  const mute = useCallback(() => {
    if (!video) return;
    video.muted = true;
    setMuted(true);
  }, [video]);

  const unmute = useCallback(() => {
    if (!video) return;
    video.muted = false;
    setMuted(false);
  }, [video]);

  const fullScreen = useCallback(() => {
    if (!video || !fullScreenEnabled) return;
    return video.requestFullscreen();
  }, [video, fullScreenEnabled]);

  const jumpToTimestamp = useCallback(
    (seconds: number) => {
      if (!video) return;
      video.currentTime = seconds;
      setCurrentTimestamp(seconds);
      setVideoReel(null);
    },
    [video]
  );

  const goToNextHighlight = useCallback(() => {
    if (!video) return;

    // If the video reel is set, advance to the next clip index
    if (videoReel && videoReelClipIndex != null) {
      const nextClipIndex = Math.min(
        videoReelClipIndex + 1,
        videoReel.clips.length
      );
      setVideoReelClipIndex(nextClipIndex);
      return;
    }

    const currentTimestamp = video.currentTime;
    const nextHighlight = highlights.find(
      (h) => (h.relativeStartTime ?? 0) > currentTimestamp
    );
    if (nextHighlight && nextHighlight.relativeStartTime != null) {
      setVideoStartAt(nextHighlight.relativeStartTime);
    }
  }, [video, videoReel, highlights, videoReelClipIndex]);

  const updatePlaybackRate = useCallback(
    (rate: number) => {
      if (!video) return;
      setPlaybackRate(rate);
      video.playbackRate = rate;
    },
    [video]
  );

  return {
    goToNextHighlight,
    jumpToTimestamp,
    mute,
    muted,
    pause,
    paused,
    fullScreenEnabled,
    play,
    fullScreen,
    updatePlaybackRate,
    unmute,
  };
};

export default useVideoControls;
